import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { useAuth } from '../auth/AuthContext';
import { useAppUi } from '../app/useAppUi';

export function AdminsPage() {
  const { t } = useAppUi();
  const { admin } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);

  const isSuper = admin?.role === 'SUPER_ADMIN';
  const [createOpen, setCreateOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [role, setRole] = React.useState('VIEWER');

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/list', {
        params: { take: pageSize, page: page + 1 },
      });
      const data = unwrapApiResponse(res);
      setRows(Array.isArray(data) ? data : []);
      setRowCount(Array.isArray(data) ? data.length : 0);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createAdmin = async () => {
    setSaving(true);
    setError(null);
    try {
      await http.post('/admin/register', { username, password, role });
      setCreateOpen(false);
      setUsername('');
      setPassword('');
      setRole('VIEWER');
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const deleteAdmin = async (id) => {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      await http.delete(`/admin/delete/${id}`);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { field: 'id', headerName: t('admins.colId'), width: 90 },
    { field: 'username', headerName: t('admins.colUsername'), flex: 1, minWidth: 240 },
    { field: 'role', headerName: t('admins.colRole'), width: 160 },
    { field: 'created_at', headerName: t('admins.colCreated'), width: 190 },
    ...(isSuper
      ? [
          {
            field: 'actions',
            headerName: t('admins.colActions'),
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: (p) => (
              <Button
                size="small"
                color="error"
                disabled={saving}
                onClick={() => deleteAdmin(p.row.id)}
              >
                {t('common.delete')}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Box>
      <PageHeader
        title={t('admins.title')}
        subtitle={t('admins.subtitle')}
        right={
          <Stack direction="row" spacing={1}>
            {isSuper ? (
              <Button variant="contained" onClick={() => setCreateOpen(true)}>
                {t('admins.create')}
              </Button>
            ) : null}
            <Button variant="outlined" onClick={fetchData} disabled={loading}>
              {t('common.refresh')}
            </Button>
          </Stack>
        }
      />

      <ErrorBanner error={error} />

      <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 2, boxShadow: (th) => th.custom.shadow, overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => {
            setPage(m.page);
            setPageSize(m.pageSize);
          }}
          disableRowSelectionOnClick
        />
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('admins.dialogCreateTitle')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t('login.username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
            />
            <TextField
              label={t('login.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <TextField select label={t('admins.role')} value={role} onChange={(e) => setRole(e.target.value)}>
              <MenuItem value="VIEWER">{t('admins.roleViewer')}</MenuItem>
              <MenuItem value="ADMIN">{t('admins.roleAdmin')}</MenuItem>
              <MenuItem value="SUPER_ADMIN">{t('admins.roleSuperAdmin')}</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={createAdmin}
            disabled={saving || !username || !password}
          >
            {saving ? t('common.creating') : t('admins.createBtn')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

